/** Browser only: low-res page previews for the poster dialog. */

import type { PDFDocumentProxy } from "pdfjs-dist";
import { PDF_OPTIONS, pdfjs } from "../pdfjs-setup.js";

/**
 * `getDocument` transfers the buffer to the worker, which detaches it — the
 * caller's bytes would be empty for the later export. pdf.js gets a copy.
 */
export async function openPdfForPreview(bytes: Uint8Array): Promise<PDFDocumentProxy> {
  return pdfjs.getDocument({ data: bytes.slice(), ...PDF_OPTIONS }).promise;
}

/**
 * Renders one page (with its `/Rotate`) to a PNG object URL whose long side is
 * at most `maxPx`. The caller revokes the URL. The canvas is released after use.
 */
export async function renderPdfPagePreview(
  doc: PDFDocumentProxy,
  pageNumber: number,
  maxPx: number,
): Promise<string> {
  const page = await doc.getPage(pageNumber);
  const canvas = document.createElement("canvas");
  try {
    // getViewport applies the page rotation unless one is passed explicitly.
    const base = page.getViewport({ scale: 1 });
    const scale = maxPx / Math.max(base.width, base.height);
    const viewport = page.getViewport({ scale });
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));
    await page.render({ canvas, viewport }).promise;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error("PDF-Vorschau konnte nicht erstellt werden.");
    return URL.createObjectURL(blob);
  } finally {
    canvas.width = 0;
    canvas.height = 0;
    page.cleanup();
  }
}
